import { ArrowRight, Clock3, MoveUpRight } from 'lucide-react'
import type { JournalTrade } from '../types'
import { dayLabel, hhmm, holdText } from '../utils/format'
import { JournalCharges } from './JournalCharges'
import { JournalLegs } from './JournalLegs'
import { JournalRoles } from './JournalRoles'
import { JournalReview } from './JournalReview'
import { JournalRules } from './JournalRules'
import { JournalTimeline } from './JournalTimeline'
import { MarketSnapshots } from './MarketSnapshots'
import { TradePathSummary } from './TradePathSummary'

export interface JournalDetailProps {
  trade: JournalTrade
  onPatched: (next: Partial<JournalTrade>) => void
}

const spot = (value: number | null) => value === null
  ? '—'
  : value.toLocaleString('en-IN', { maximumFractionDigits: 2 })

/** Leg identity stays independent of strategy names, including in snapshot selection. */
export function JournalDetail({ trade, onPatched }: JournalDetailProps) {
  return (
    <div className="jn-detail" id={`journal-detail-${trade.tradeId}`} data-testid={`journal-detail-${trade.tradeId}`}>
      <div className="jn-context-bar">
        <div className="jn-context-time">
          <Clock3 size={13} />
          <span className="num">
            {dayLabel(trade.openedAt)} · {hhmm(trade.openedAt)} <ArrowRight size={11} />
            {trade.closedAt && dayLabel(trade.closedAt) !== dayLabel(trade.openedAt) ? `${dayLabel(trade.closedAt)} · ` : ''}
            {hhmm(trade.closedAt)}
          </span>
          <span className="jn-badge">{holdText(trade.holdMinutes, trade.openedAt)}</span>
        </div>
        <div className="jn-context-spot">
          <MoveUpRight size={13} />
          <span>{trade.underlying} spot</span>
          <span className="num">{spot(trade.spotAtEntry)} <ArrowRight size={11} /> {spot(trade.spotAtExit)}</span>
        </div>
      </div>
      <div className="jn-detail-grid">
        <div className="jn-detail-main">
          <JournalLegs legs={trade.legs} />
          <MarketSnapshots trade={trade} />
        </div>
        <aside className="jn-detail-aside" aria-label="Trade analysis and review">
          <TradePathSummary path={trade.path} isDemo={trade.isDemo} />
          <JournalRoles legs={trade.legs} />
          <JournalRules trade={trade} />
          {/*
            * A FOLDED GROUP IS NOBODY'S REVIEW — 9.23. Its `tradeId` is the
            * group's key and names no row in the database, so a note saved
            * here would have nowhere to go. The legs, charges and timeline
            * above are all real and stay.
            */}
          {trade.isGroup ? null : <JournalReview trade={trade} onPatched={onPatched} />}
          <JournalCharges trade={trade} />
        </aside>
      </div>
      <JournalTimeline events={trade.events} activity={trade.activity} />
    </div>
  )
}
